import React, { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Bug, Lightbulb, Loader2 } from 'lucide-react';
import { useAuth } from '../../../utils/AuthContext';
import {
  ensureCrmFeedbackNumber,
  formatCrmBugLabel,
  subscribeCrmFeedback,
  updateCrmFeedback,
} from '../../../utils/crmFeedbackService';
import type { CrmFeedback, CrmFeedbackStatus, CrmFeedbackType } from '../../../types/crmFeedback';
import SettingsSectionShell from '../SettingsSectionShell';
import './FeedbackSettingsSection.css';

type TypeFilter = 'all' | CrmFeedbackType;
type StatusFilter = 'all' | CrmFeedbackStatus;

function pagePath(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url || '—';
  }
}

function formatWhen(item: CrmFeedback): string {
  try {
    return formatDistanceToNow(item.createdAt.toDate(), { addSuffix: true });
  } catch {
    return '';
  }
}

const FeedbackSettingsSection: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<CrmFeedback[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  useEffect(() => {
    return subscribeCrmFeedback(
      next => {
        setItems(next);
        setError(null);
      },
      err => setError(err instanceof Error ? err.message : 'Could not load feedback.'),
    );
  }, []);

  useEffect(() => {
    if (!items) return;
    const missing = items
      .filter(item => !item.number)
      .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const item of missing) {
        if (cancelled) return;
        try {
          await ensureCrmFeedbackNumber(item.id);
        } catch (err) {
          console.warn('[crmFeedback] Could not assign bug number', item.id, err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter(item => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      return true;
    });
  }, [items, typeFilter, statusFilter]);

  const selected = filtered.find(item => item.id === selectedId) ?? filtered[0] ?? null;

  useEffect(() => {
    if (selected) {
      setNote(selected.adminNote);
      setLogsOpen(false);
    }
  }, [selected?.id, selected?.adminNote]);

  if (user?.role !== 'admin') return null;

  const saveNote = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await updateCrmFeedback(selected.id, { adminNote: note.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save note.');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: CrmFeedbackStatus) => {
    if (!selected) return;
    setBusy(true);
    try {
      await updateCrmFeedback(selected.id, { status, adminNote: note.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update status.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsSectionShell
      title="Feedback"
      description="Bug reports and feature requests submitted from the CRM header."
    >
      {error && <p className="fb-inbox-msg fb-inbox-msg--err">{error}</p>}

      <div className="fb-inbox">
        <div className="fb-inbox-toolbar">
          <div className="fb-inbox-filters" role="group" aria-label="Type">
            {(['all', 'bug', 'feature'] as const).map(value => (
              <button
                key={value}
                type="button"
                className={`fb-inbox-chip${typeFilter === value ? ' is-active' : ''}`}
                onClick={() => setTypeFilter(value)}
              >
                {value === 'all' ? 'All' : value === 'bug' ? 'Bugs' : 'Features'}
              </button>
            ))}
          </div>
          <div className="fb-inbox-filters" role="group" aria-label="Status">
            {(['all', 'open', 'done'] as const).map(value => (
              <button
                key={value}
                type="button"
                className={`fb-inbox-chip${statusFilter === value ? ' is-active' : ''}`}
                onClick={() => setStatusFilter(value)}
              >
                {value === 'all' ? 'All statuses' : value === 'open' ? 'Open' : 'Done'}
              </button>
            ))}
          </div>
        </div>

        {items === null ? (
          <div className="fb-inbox-empty">
            <Loader2 size={16} className="fb-inbox-spin" aria-hidden />
            Loading feedback…
          </div>
        ) : filtered.length === 0 ? (
          <div className="fb-inbox-empty">No matching reports.</div>
        ) : (
          <div className="fb-inbox-split">
            <ul className="fb-inbox-list">
              {filtered.map(item => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`fb-inbox-row${selected?.id === item.id ? ' is-selected' : ''}`}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <span className="fb-inbox-row-chips">
                      {formatCrmBugLabel(item.number) && (
                        <span className="fb-inbox-number">{formatCrmBugLabel(item.number)}</span>
                      )}
                      <span className={`fb-inbox-type fb-inbox-type--${item.type}`}>
                        {item.type === 'bug' ? <Bug size={12} aria-hidden /> : <Lightbulb size={12} aria-hidden />}
                        {item.type === 'bug' ? 'Bug' : 'Feature'}
                      </span>
                    </span>
                    <span className="fb-inbox-row-title">{item.description || 'Untitled'}</span>
                    <span className="fb-inbox-row-meta">
                      {item.reporterName || item.reporterEmail} · {pagePath(item.pageUrl)} · {formatWhen(item)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {selected && (
              <article className="fb-inbox-detail">
                <header className="fb-inbox-detail-head">
                  {formatCrmBugLabel(selected.number) && (
                    <span className="fb-inbox-number">{formatCrmBugLabel(selected.number)}</span>
                  )}
                  <span className={`fb-inbox-type fb-inbox-type--${selected.type}`}>
                    {selected.type === 'bug' ? 'Bug' : 'Feature'}
                  </span>
                  <span className={`fb-inbox-status fb-inbox-status--${selected.status}`}>
                    {selected.status === 'open' ? 'Open' : 'Done'}
                  </span>
                </header>
                {formatCrmBugLabel(selected.number) && (
                  <p className="fb-inbox-note-hint">
                    In Cursor: pull {selected.type === 'bug' ? 'bug' : 'feature'} {formatCrmBugLabel(selected.number)} and fix it
                  </p>
                )}
                <p className="fb-inbox-desc">{selected.description}</p>
                <dl className="fb-inbox-meta">
                  <div>
                    <dt>From</dt>
                    <dd>{selected.reporterName} ({selected.reporterEmail})</dd>
                  </div>
                  <div>
                    <dt>Page</dt>
                    <dd>{pagePath(selected.pageUrl)}</dd>
                  </div>
                  <div>
                    <dt>When</dt>
                    <dd>{selected.createdAt.toDate().toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Viewport</dt>
                    <dd>{selected.viewport.w}×{selected.viewport.h}</dd>
                  </div>
                </dl>

                {selected.screenshotUrl && (
                  <a
                    className="fb-inbox-shot"
                    href={selected.screenshotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={selected.screenshotUrl} alt="Submitted screenshot" />
                  </a>
                )}

                {selected.logs.length > 0 && (
                  <div className="fb-inbox-logs">
                    <button type="button" className="fb-inbox-logs-toggle" onClick={() => setLogsOpen(v => !v)}>
                      {logsOpen ? 'Hide' : 'Show'} browser logs ({selected.logs.length})
                    </button>
                    {logsOpen && (
                      <pre className="fb-inbox-logs-body">
                        {selected.logs.map(log => `[${log.t}] ${log.level.toUpperCase()} ${log.message}`).join('\n')}
                      </pre>
                    )}
                  </div>
                )}

                <label className="fb-inbox-note">
                  {selected.type === 'bug' ? 'Note to reporter' : 'Admin note'}
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    rows={3}
                    placeholder={
                      selected.type === 'bug'
                        ? 'Optional — this is included in their notification when you mark the bug done'
                        : 'Optional note for other admins'
                    }
                  />
                </label>
                {selected.type === 'bug' && selected.status === 'open' && (
                  <p className="fb-inbox-note-hint">
                    The reporter will see this note in their notification bell.
                  </p>
                )}

                <div className="fb-inbox-actions">
                  <button type="button" className="pm-btn pm-btn-ghost pm-btn-sm" onClick={saveNote} disabled={busy}>
                    Save note
                  </button>
                  {selected.status === 'open' ? (
                    <button type="button" className="pm-btn pm-btn-primary pm-btn-sm" onClick={() => setStatus('done')} disabled={busy}>
                      Mark done
                    </button>
                  ) : (
                    <button type="button" className="pm-btn pm-btn-ghost pm-btn-sm" onClick={() => setStatus('open')} disabled={busy}>
                      Reopen
                    </button>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </div>
    </SettingsSectionShell>
  );
};

export default FeedbackSettingsSection;
